/**
 * Orchestrates one auto-schedule run: read the sites, plan a draft, and have
 * the database dry-run it until nothing in it breaches a rule.
 *
 * The engine owns no rule knowledge and no Supabase access. The planner
 * forecasts; `previewPlan` judges. Whatever the dry run refuses is ruled out
 * and the draft is planned again, and if planning cannot get clear the engine
 * stops handing work out and starts taking it back, which can only reduce the
 * breaches. A draft that still will not verify is never returned.
 */

import { blameAssignments, namedGuardIds } from './blame';
import { buildDemand } from './demand';
import { exclusionKey, planSchedule } from './planner';
import { draftTotals, summariseWeeks, toPlanPayload } from './summary';
import type {
  ApplyResult,
  AutoScheduleGateway,
  PlanResult,
  PlanViolation,
  ScheduleDraft,
  ScheduleOptions,
  UnfilledBlock,
} from './types';

export interface AutoScheduleEngine {
  /** Plans and verifies a draft for every site in the list at once. Writes nothing. */
  draft(siteIds: string[], options: ScheduleOptions): Promise<ScheduleDraft>;
  /** Saves a draft as unpublished roster items; the database checks it once more. */
  apply(draft: ScheduleDraft): Promise<ApplyResult>;
}

export interface AutoScheduleEngineOptions {
  gateway: AutoScheduleGateway;
  /** Ids for the posts a draft creates. */
  newId?: () => string;
  /** Planning rounds before the engine stops re-planning and withdraws instead. */
  maxPlanningRounds?: number;
}

/** The dry run kept finding breaches the engine could not plan or withdraw its way out of. */
export class UnverifiedDraftError extends Error {
  violations: PlanViolation[];

  constructor(violations: PlanViolation[]) {
    super('The draft could not be verified against the roster rules');
    this.name = 'UnverifiedDraftError';
    this.violations = violations;
  }
}

const DEFAULT_PLANNING_ROUNDS = 8;
const MAX_WITHDRAWAL_ROUNDS = 12;

function randomUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // RFC 4122 version 4, for runtimes without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    return (char === 'x' ? value : (value % 4) + 8).toString(16);
  });
}

const byTime = (a: UnfilledBlock, b: UnfilledBlock): number => (
  a.block.slotDate.localeCompare(b.block.slotDate)
  || a.block.startMinute - b.block.startMinute
  || a.block.lane - b.block.lane
  || a.block.key.localeCompare(b.block.key)
);

export function createAutoScheduleEngine({
  gateway,
  newId = randomUuid,
  maxPlanningRounds = DEFAULT_PLANNING_ROUNDS,
}: AutoScheduleEngineOptions): AutoScheduleEngine {
  return {
    async draft(requestedSiteIds, options) {
      const siteIds = Array.from(new Set(requestedSiteIds));
      const context = await gateway.loadContext(siteIds, options);
      const days = buildDemand(context, options, newId);
      const excluded = new Set<string>();

      // Open posts breach nothing, so a draft that gives nobody work needs no dry run.
      const check = async (result: PlanResult): Promise<PlanViolation[]> => (
        result.assignments.length === 0
          ? []
          : (await gateway.previewPlan(siteIds, toPlanPayload(days, result.assignments))).violations
      );

      let result = planSchedule({ context, days, excluded });
      let violations = await check(result);
      let rounds = 1;

      while (violations.length > 0 && rounds < maxPlanningRounds) {
        const refused = blameAssignments(violations, result.assignments);
        if (refused.size === 0) break;
        refused.forEach((key) => excluded.add(key));
        result = planSchedule({ context, days, excluded });
        violations = await check(result);
        rounds += 1;
      }

      // Taking work away cannot create a breach, so from here the draft only
      // shrinks: first the shifts the breaches point at, then everything the
      // named guards were given.
      for (let withdrawal = 0; violations.length > 0; withdrawal += 1) {
        if (withdrawal >= MAX_WITHDRAWAL_ROUNDS) throw new UnverifiedDraftError(violations);

        const refused = withdrawal === 0 ? blameAssignments(violations, result.assignments) : new Set<string>();
        const named = namedGuardIds(violations);
        const withdrawn = result.assignments.filter((assignment) => (
          refused.size > 0
            ? refused.has(exclusionKey(assignment.guardId, assignment.block.key))
            : named.has(assignment.guardId)
        ));
        if (withdrawn.length === 0) throw new UnverifiedDraftError(violations);

        const taken = new Set(withdrawn);
        result = {
          assignments: result.assignments.filter((assignment) => !taken.has(assignment)),
          unfilled: [
            ...result.unfilled,
            ...withdrawn.map((assignment): UnfilledBlock => ({ block: assignment.block, reasons: { rule_check: 1 } })),
          ].sort(byTime),
        };
        violations = await check(result);
        rounds += 1;
      }

      const draft: ScheduleDraft = {
        siteIds,
        context,
        options,
        days,
        assignments: result.assignments,
        unfilled: result.unfilled,
        weeks: summariseWeeks(context, options, days, result.assignments),
        totals: draftTotals(days, result.assignments, result.unfilled),
        verificationRounds: rounds,
        plan: toPlanPayload(days, result.assignments),
      };
      return draft;
    },

    apply(draft) {
      return gateway.applyPlan(draft.siteIds, draft.plan);
    },
  };
}
